// Iter53 — Toolbox Status Dashboard tile.
//
// Read-only consumer of `GET /api/forge/toolbox/modules-status`. Renders
// the ARXMENTIS toolbox inventory, 5-rank hierarchy, market-condition
// routing matrix, preview-ready endpoints, protected core, and deferred
// capabilities. Pure visual layer.
//
// IMPORTANT:
//  • This tile MUST NOT call any preview/evaluator endpoint.
//  • It MUST NOT trigger scans or write to the journal.
//  • Single endpoint contract: GET /api/forge/toolbox/modules-status.

import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Activity, AlertTriangle, Crown, EyeOff, Layers,
  Lock, Map as MapIcon, ShieldCheck, ListChecks, RefreshCw,
} from "lucide-react";
import { API } from "@/App";

const REFRESH_INTERVAL_MS = 60_000;     // 60s — same cadence family as Parity Health

const STATUS_BADGE = {
  protected:     { label: "Protected",     cls: "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]" },
  preview_ready: { label: "Preview-ready", cls: "border-[#007AFF]/40 bg-[#007AFF]/10 text-[#007AFF]" },
  active:        { label: "Active",        cls: "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]" },
  planned:       { label: "Planned",       cls: "border-[#5F5F66]/40 bg-[#5F5F66]/10 text-[#8A8A93]" },
  deferred:      { label: "Deferred",      cls: "border-[#5F5F66]/40 bg-[#5F5F66]/10 text-[#8A8A93]" },
};

const COUNT_ORDER = ["protected", "preview_ready", "active", "planned", "deferred"];

// Friendly market-condition labels (display only).
const COND_LABEL = {
  SUPPORT_ACCEPTANCE:   "Support Acceptance",
  RESISTANCE_REJECTION: "Resistance Rejection",
  INVENTORY_MANAGEMENT: "Inventory Management",
  VALUE_COMPRESSION:    "Value Compression",
  TREND_EXPANSION:      "Trend Expansion",
  EVENT_RISK:           "Event Risk",
  UNCLEAR:              "Unclear",
};

// Compact friendly module labels for chips inside the matrix.
const MODULE_SHORT = {
  WHEEL_ITM_CRSP:          "Wheel ITM",
  WHEEL_ITM_LADDER:        "Wheel Ladder",
  REGIME_SELECTOR_PREVIEW: "Regime Selector",
  BPCS:                    "BPCS",
  BCCS:                    "BCCS",
  IRON_CONDOR:             "Iron Condor",
  CFM_CC_SHARES:           "CFM Shares",
  CFM_CC_LEAPS:            "CFM LEAPS",
  NO_TRADE:                "NO TRADE",
};

function StatusBadge({ status }) {
  const meta = STATUS_BADGE[status] || STATUS_BADGE.planned;
  return (
    <span
      data-testid={`toolbox-status-badge-${status}`}
      className={`px-2 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-[0.14em] ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
}

function ModuleChip({ id }) {
  return (
    <span
      data-testid={`toolbox-status-module-chip-${id}`}
      className="px-2 py-0.5 rounded-sm border border-[#26262B] bg-[#121216] text-[10px] font-mono text-[#E0E0E0]"
    >
      {MODULE_SHORT[id] || id}
    </span>
  );
}

export default function ToolboxStatusTile() {
  const [data, setData]     = useState(null);
  const [err, setErr]       = useState(null);
  const [loading, setLoad]  = useState(true);
  const [refreshing, setR]  = useState(false);

  const load = useCallback(async (opts = {}) => {
    if (opts.silent) setR(true); else setLoad(true);
    try {
      const r = await axios.get(`${API}/forge/toolbox/modules-status`);
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Network error");
    } finally {
      setLoad(false);
      setR(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => load({ silent: true }), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  // ── Loading state ─────────────────────────────────────────────────
  if (loading && !data) {
    return (
      <div data-testid="toolbox-status-tile-loading"
           className="px-4 py-3 mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E] text-[10px] font-mono text-[#5F5F66]">
        Market Toolbox · loading…
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────
  if (err && !data) {
    return (
      <div data-testid="toolbox-status-tile-error"
           className="flex items-center gap-2 px-4 py-3 mb-5 rounded-sm border border-[#FF3366]/30 bg-[#FF3366]/5 text-[10px] font-mono text-[#FF3366]">
        <AlertTriangle size={14} />
        <span>Market Toolbox · {err}</span>
        <button
          data-testid="toolbox-status-tile-retry"
          onClick={() => load()}
          className="ml-auto inline-flex items-center gap-1 text-[#007AFF] hover:underline"
        >
          <RefreshCw size={12} /> Retry
        </button>
      </div>
    );
  }

  // ── Empty state (no modules) ─────────────────────────────────────
  if (!data || !Array.isArray(data.modules) || data.modules.length === 0) {
    return (
      <div data-testid="toolbox-status-tile-empty"
           className="px-4 py-3 mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E] text-[10px] font-mono text-[#8A8A93]">
        Market Toolbox · no modules registered yet.
      </div>
    );
  }

  const {
    toolbox_version, read_only, hierarchy = [],
    market_condition_matrix = {}, modules = [],
    protected_core = {}, preview_endpoints = [],
    deferred_items = [], counts_by_status = {},
  } = data;

  const previewModules = modules.filter(m => m.status === "preview_ready");
  const protectedModules = modules.filter(m => m.status === "protected");

  return (
    <div
      data-testid="toolbox-status-tile"
      data-version={toolbox_version}
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#26262B]">
        <Layers size={16} className="text-[#00FF88]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          Market Toolbox
        </span>
        <span data-testid="toolbox-status-version"
              className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · {toolbox_version}
        </span>

        {read_only && (
          <span
            data-testid="toolbox-status-readonly-badge"
            className="px-2 py-0.5 rounded-sm border border-[#00FF88]/40 bg-[#00FF88]/10 text-[9px] font-mono uppercase tracking-[0.14em] text-[#00FF88] inline-flex items-center gap-1"
            title="This endpoint is read-only metadata. No execution, no journal writes, no broker calls."
          >
            <EyeOff size={10} /> Read-only
          </span>
        )}
        <span
          data-testid="toolbox-status-no-execution"
          className="px-2 py-0.5 rounded-sm border border-[#26262B] bg-[#121216] text-[9px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]"
          title="Every toolbox module reports execution_enabled = false."
        >
          No execution enabled
        </span>

        <div className="ml-auto flex items-center gap-2 text-[9px] font-mono">
          {refreshing && <Loader />}
          {COUNT_ORDER.map(k => (
            counts_by_status[k] !== undefined && (
              <div key={k}
                   data-testid={`toolbox-status-count-${k}`}
                   className="flex items-center gap-1 text-[#8A8A93]">
                <span className="opacity-70 uppercase tracking-[0.12em]">{STATUS_BADGE[k]?.label || k}</span>
                <span className="font-bold text-[#E0E0E0]">{counts_by_status[k]}</span>
              </div>
            )
          ))}
        </div>
      </div>

      {/* ── Body grid: hierarchy + matrix + chips ───────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-[#26262B]">
        {/* Hierarchy */}
        <section data-testid="toolbox-status-hierarchy" className="px-4 py-3">
          <SectionTitle icon={<Crown size={12} />} label="Strategy Hierarchy" />
          <ol className="mt-2 space-y-1.5">
            {hierarchy.map(h => (
              <li
                key={h.rank}
                data-testid={`toolbox-status-hierarchy-rank-${h.rank}`}
                className="flex items-start gap-2 text-[11px] font-mono text-[#E0E0E0]"
              >
                <span className="text-[#5F5F66] tabular-nums w-4">{h.rank}.</span>
                <div className="flex-1">
                  <div className="text-[#E0E0E0]">
                    {(MODULE_SHORT[h.module_id]) || h.module_id.replaceAll("_", " ")}
                    {Array.isArray(h.sub_modules) && h.sub_modules.length > 0 && (
                      <span className="ml-2 inline-flex gap-1">
                        {h.sub_modules.map(sm => <ModuleChip key={sm} id={sm} />)}
                      </span>
                    )}
                  </div>
                  {h.rule && (
                    <div className="text-[9px] text-[#5F5F66] mt-0.5 leading-relaxed">
                      {h.rule}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-3 text-[9px] text-[#FFB800] font-mono">
            NO_TRADE is valid capital preservation.
          </div>
        </section>

        {/* Market-condition matrix */}
        <section data-testid="toolbox-status-matrix" className="px-4 py-3">
          <SectionTitle icon={<MapIcon size={12} />} label="Market-Condition Matrix" />
          <ul className="mt-2 space-y-1.5">
            {Object.entries(market_condition_matrix).map(([cond, meta]) => (
              <li
                key={cond}
                data-testid={`toolbox-status-matrix-${cond}`}
                className="flex items-start gap-2 text-[11px] font-mono"
              >
                <span className="text-[#8A8A93] w-[160px] shrink-0">
                  {COND_LABEL[cond] || cond}
                </span>
                <span className="text-[#5F5F66]">→</span>
                <span className="flex flex-wrap gap-1">
                  {(meta.preferred_modules || []).map(m => (
                    <ModuleChip key={m} id={m} />
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* ── Preview-ready / Protected / Deferred ────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-t border-[#26262B] divide-y lg:divide-y-0 lg:divide-x divide-[#26262B]">
        <section data-testid="toolbox-status-preview-modules" className="px-4 py-3">
          <SectionTitle icon={<ListChecks size={12} />} label={`Preview-Ready (${previewModules.length})`} />
          <ul className="mt-2 space-y-1.5">
            {previewModules.map(m => (
              <li
                key={m.module_id}
                data-testid={`toolbox-status-preview-module-${m.module_id}`}
                className="flex items-center gap-2 text-[11px] font-mono"
              >
                <ModuleChip id={m.module_id} />
                <StatusBadge status={m.status} />
                {m.endpoint && (
                  <span className="text-[9px] text-[#5F5F66] font-mono truncate ml-auto"
                        title={m.endpoint}>
                    {m.endpoint.replace("/api/forge/toolbox/", "")}
                  </span>
                )}
              </li>
            ))}
            {previewModules.length === 0 && (
              <li className="text-[10px] text-[#5F5F66] font-mono">
                None yet.
              </li>
            )}
          </ul>
        </section>

        <section data-testid="toolbox-status-protected-core" className="px-4 py-3">
          <SectionTitle icon={<Lock size={12} />} label="Protected Core" />
          <ul className="mt-2 space-y-1.5">
            {Object.entries(protected_core)
              .filter(([k]) => k !== "this_endpoint_disclaimer")
              .map(([key, val]) => (
                <li
                  key={key}
                  data-testid={`toolbox-status-protected-${key}`}
                  className="flex items-start gap-2 text-[11px] font-mono"
                >
                  <ShieldCheck size={11} className="text-[#00FF88] mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-[#E0E0E0]">
                      {key.replaceAll("_", " ")}
                    </div>
                    {val?.description && (
                      <div className="text-[9px] text-[#5F5F66] leading-relaxed">
                        {val.description}
                      </div>
                    )}
                  </div>
                </li>
              ))}
          </ul>
          {protected_core.this_endpoint_disclaimer && (
            <div className="mt-3 text-[9px] text-[#5F5F66] font-mono leading-relaxed border-t border-[#26262B] pt-2">
              {protected_core.this_endpoint_disclaimer}
            </div>
          )}
          {protectedModules.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {protectedModules.map(m => <ModuleChip key={m.module_id} id={m.module_id} />)}
            </div>
          )}
        </section>

        <section data-testid="toolbox-status-deferred" className="px-4 py-3">
          <SectionTitle icon={<Activity size={12} />} label={`Deferred (${deferred_items.length})`} />
          <ul className="mt-2 space-y-1.5">
            {deferred_items.map(d => (
              <li
                key={d.id}
                data-testid={`toolbox-status-deferred-${d.id}`}
                className="flex items-start gap-2 text-[11px] font-mono"
              >
                <StatusBadge status="deferred" />
                <div className="flex-1">
                  <div className="text-[#E0E0E0]">
                    {d.id.replaceAll("_", " ")}
                  </div>
                  <div className="text-[9px] text-[#5F5F66] leading-relaxed">
                    {d.description}
                    {d.blocked_by && (
                      <span className="ml-1 text-[#FFB800]">
                        · blocked_by: {d.blocked_by.replaceAll("_", " ")}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* ── Footer: preview endpoint paths ───────────────────────── */}
      {preview_endpoints.length > 0 && (
        <div
          data-testid="toolbox-status-preview-endpoints"
          className="px-4 py-2 border-t border-[#26262B] text-[9px] font-mono text-[#5F5F66] flex flex-wrap gap-x-3 gap-y-1"
        >
          <span className="text-[#8A8A93] uppercase tracking-[0.14em]">Endpoints:</span>
          {preview_endpoints.map(e => (
            <span
              key={e.path}
              data-testid={`toolbox-status-endpoint-${e.module_id}`}
              title={`${e.module_id} preview`}
            >
              {e.path}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">
      {icon}
      {label}
    </div>
  );
}

function Loader() {
  return (
    <span
      data-testid="toolbox-status-tile-refreshing"
      className="inline-flex items-center gap-1 text-[#5F5F66]"
    >
      <RefreshCw size={10} className="animate-spin" />
      <span className="uppercase tracking-[0.14em]">refresh</span>
    </span>
  );
}
